/**
 * In-call header search.
 *
 * On the call page a navigation unmounts the conference and drops the call,
 * so the header search never navigates that page:
 * - once the call is joined, its layout is registered as the receiver for the
 *   call id (useInCallSearchTab) and the query runs in the call's own
 *   "Search Databases" tab;
 * - before that (pre-join, or the moment of joining) /search opens in a new tab.
 *
 * The request is held here until the search panel for the same call takes it,
 * because the panel is only mounted while its tab is showing. The latest
 * request wins: a second query submitted before the panel mounts replaces the
 * first, as typing a new search would. Leaving the call discards anything held.
 */

export const IN_CALL_SEARCH_EVENT = 'engageiq:in-call-search';

/** The search panel does not run shorter queries (UniversalSearchPanel). */
export const MIN_IN_CALL_QUERY_CHARS = 2;

export interface InCallSearchRequest {
  callId: string;
  query: string;
}

// Registered layouts per call, oldest first. Two mounted layouts of the same
// call (a remount overlapping its predecessor) must not unregister each other,
// and only the newest one's panel takes a request.
const receivers = new Map<string, symbol[]>();
const receiverListeners = new Set<() => void>();
let pending: InCallSearchRequest | null = null;

function notifyReceivers(): void {
  receiverListeners.forEach((listener) => listener());
}

/** The call id of /calls/<id> (the call page itself), else null. */
export function callIdFromPath(pathname: string | null | undefined): string | null {
  const match = /^\/calls\/([^/]+)\/?$/.exec(pathname ?? '');
  return match ? decodeURIComponent(match[1]) : null;
}

/** The call id whose joined call can take a header search for this page, else null. */
export function inCallSearchReceiverFor(pathname: string | null | undefined): string | null {
  const callId = callIdFromPath(pathname);
  return callId !== null && (receivers.get(callId)?.length ?? 0) > 0 ? callId : null;
}

/** For useSyncExternalStore: re-render when a call registers or leaves. */
export function subscribeInCallSearchReceivers(listener: () => void): () => void {
  receiverListeners.add(listener);
  return () => receiverListeners.delete(listener);
}

export function registerInCallSearchReceiver(callId: string, token: symbol): () => void {
  receivers.set(callId, [...(receivers.get(callId) ?? []), token]);
  notifyReceivers();
  let registered = true;
  return () => {
    if (!registered) return;
    registered = false;
    const left = (receivers.get(callId) ?? []).filter((t) => t !== token);
    if (left.length > 0) {
      receivers.set(callId, left);
    } else {
      receivers.delete(callId);
      if (pending?.callId === callId) pending = null;
    }
    notifyReceivers();
  };
}

/** True for the most recently registered layout of this call: only its panel takes a request. */
export function isNewestInCallSearchReceiver(callId: string, token: symbol | null): boolean {
  return token !== null && (receivers.get(callId) ?? []).at(-1) === token;
}

/** Hands the query to the call. False (nothing sent) for a query the panel would not run. */
export function requestInCallSearch(callId: string, query: string): boolean {
  const trimmed = query.trim();
  if (trimmed.length < MIN_IN_CALL_QUERY_CHARS) return false;
  pending = { callId, query: trimmed };
  window.dispatchEvent(new CustomEvent<InCallSearchRequest>(IN_CALL_SEARCH_EVENT, { detail: pending }));
  return true;
}

/** Returns this call's held request once, then clears it; another call's request is left alone. */
export function takePendingInCallSearch(callId: string): InCallSearchRequest | null {
  if (!pending || pending.callId !== callId) return null;
  const request = pending;
  pending = null;
  return request;
}
